import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StreamerPlaylist } from '@/components/StreamerPlaylist';
import { StreamerPlayerControls } from '@/components/streamer/StreamerPlayerControls';
import { StreamerNormalizationIndicator } from '@/components/streamer/StreamerNormalizationIndicator';
import { useAudioContext } from '@/contexts/AudioContext';
import { Button } from '@/components/ui/button';
import { Palette, Keyboard } from 'lucide-react';
import ShortcutsDialog from './ShortcutsDialog';
import { createPortal } from 'react-dom';

const visualizers = {
  cosmic: 'Cosmic Waves',
  mirror: 'Mirror Spectrum',
  frosty: 'Frosty Waves',
  dual: 'Dual Core',
  ambient: 'Ambient Spectrum',
  sandy: 'Sandy Dunes',
  citywarp: 'City Warp',
  randomize: 'Randomize',
};

export function StreamerPlayer({
  currentSong,
  isPlaying,
  currentTime,
  duration,
  volume,
  isLooping,
  isPlaylistLooping,
  isMinimized,
  playlist,
  currentIndex,
  isPlaylistCollapsed,
  onPlayPause,
  onSeek,
  onVolumeChange,
  onToggleLoop,
  onTogglePlaylistLoop,
  onNext,
  onPrevious,
  onToggleMinimize,
  onTogglePlaylist,
  onFileUpload,
  onSongSelect,
  onSongRemove,
  onReorder,
  onSavePlaylist,
  visualizerType,
  setVisualizerType
}) {
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(volume);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showVisualizerMenu, setShowVisualizerMenu] = useState(false);
  
  // Get volume normalization data from context
  const { normalizationApplied, showNormalizationIndicator } = useAudioContext();

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVolumeToggle = () => {
    if (isMuted) {
      onVolumeChange(previousVolume);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume);
      onVolumeChange(0);
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (newVolume) => {
    onVolumeChange(newVolume[0]);
    setIsMuted(newVolume[0] === 0);
  };

  const handleTogglePlaylist = () => {
    console.log('🎯 StreamerPlayer: Toggle playlist called, current state:', isPlaylistCollapsed);
    onTogglePlaylist();
  };

  // Remove the floating visualizer switcher and shortcuts button from the player area
  return (
    <div className="h-full flex flex-col relative bg-black">
      {/* Normalization Indicator */}
      <StreamerNormalizationIndicator
        showNormalizationIndicator={showNormalizationIndicator}
        normalizationApplied={normalizationApplied}
        isMinimized={isMinimized}
      />
      {/* Floating Playlist Panel - Reduced z-index to not interfere with visualizer */}
      <AnimatePresence>
        {!isPlaylistCollapsed && (
          <motion.div
            initial={{ 
              opacity: 0, 
              scale: 0.8,
              x: -50,
              y: 50
            }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              x: 0,
              y: 0
            }}
            exit={{ 
              opacity: 0, 
              scale: 0.8,
              x: -50,
              y: 50
            }}
            transition={{ 
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.4
            }}
            className="fixed left-4 bottom-20 z-50"
            style={{
              width: '420px',
              height: '500px',
              maxWidth: 'calc(100vw - 2rem)',
              maxHeight: 'calc(100vh - 200px)'
            }}
          >
            <div 
              className="w-full h-full bg-black/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden"
              style={{
                position: 'relative'
              }}
            >
              {/* Floating Panel Header - Condensed */}
              <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-b border-white/10 px-4 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse"></div>
                    <h3 className="text-base font-bold text-white">
                      Playlist
                    </h3>
                    <span className="text-xs text-white/60 bg-white/10 px-2 py-0.5 rounded-full">
                      {playlist.length} track{playlist.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  <button
                    onClick={handleTogglePlaylist}
                    className="text-white/60 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-all duration-200"
                    title="Close Playlist"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Floating Panel Content - Removed bottom padding completely */}
              <div className="h-full overflow-hidden">
                <StreamerPlaylist
                  playlist={playlist}
                  currentIndex={currentIndex}
                  isPlaying={isPlaying}
                  onFileUpload={onFileUpload}
                  onSongSelect={onSongSelect}
                  onSongRemove={onSongRemove}
                  onReorder={onReorder}
                  onSavePlaylist={onSavePlaylist}
                  onPlay={onPlayPause}
                  onPause={onPlayPause}
                  onCollapse={handleTogglePlaylist}
                  isFloating={true}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Player Controls */}
      <StreamerPlayerControls
        currentSong={currentSong}
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        volume={volume}
        isLooping={isLooping}
        isPlaylistLooping={isPlaylistLooping}
        isMinimized={isMinimized}
        playlist={playlist}
        isPlaylistCollapsed={isPlaylistCollapsed}
        isMuted={isMuted}
        onPlayPause={onPlayPause}
        onSeek={onSeek}
        onVolumeToggle={handleVolumeToggle}
        onVolumeChange={handleVolumeChange}
        onToggleLoop={onToggleLoop}
        onTogglePlaylistLoop={onTogglePlaylistLoop}
        onNext={onNext}
        onPrevious={onPrevious}
        onToggleMinimize={onToggleMinimize}
        onTogglePlaylist={handleTogglePlaylist}
        formatTime={formatTime}
        showShortcuts={showShortcuts}
        setShowShortcuts={setShowShortcuts}
      />
      {showShortcuts && createPortal(
        <ShortcutsDialog open={showShortcuts} onClose={() => setShowShortcuts(false)} />, document.body
      )}
    </div>
  );
}